import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import Sidebar from '../components/layout/Sidebar'
import { agendaVoiceApi } from '../services/agendaVoiceApi'
import { meetingsAPI } from '../services/api'
import {
  agendaVoiceSttApi,
  checkApiReachable,
  getSttWebSocketUrl,
} from '../services/agendaVoiceSttApi'
import { ttsApi } from '../services/ttsApi'
import { getTtsProfileClient } from '../services/ttsProfileClient'
import { applyTtsPlaybackSettings } from '../services/ttsPlaybackSettings'
import { useToast } from '../components/common/Toast'
import {
  formatVoiceAgendaClearCommands,
  formatVoiceAgendaCommitCommands,
  getVoiceAgendaClearCommands,
  getVoiceAgendaCommitCommands,
} from '../config/voiceAgendaConfig'
import { DEFAULT_TTS_PROFILE } from '@core/tts/ttsProfile'
import { VOICE_AGENDA_WELCOME_MESSAGE } from '@core/config/templates/voiceAgendaWelcome'
import { VOICE_AGENDA_WELCOME_AUDIO_URL } from '../constants/voiceAgendaWelcomeAsset'
import { decodeHtmlEntities } from '../utils/displayText'

const stepLabels = {
  meeting_type: 'Meeting Type',
  meeting_title: 'Meeting Title',
  meeting_duration_capture: 'Meeting Duration',
  recurrence_flag: 'Recurrence',
  agenda_item_capture: 'Agenda Items',
  agenda_item_duration: 'Item Duration',
  agenda_review: 'Review & Save',
  finalized: 'Saved',
}

const stepPlaceholders = {
  meeting_type: 'Say or select a meeting type',
  meeting_title: 'e.g. Q2 planning sync',
  meeting_duration_capture: 'e.g. 45 minutes',
  recurrence_flag: 'One-time or recurring',
  agenda_item_capture: 'e.g. Budget review',
  agenda_item_duration: 'Time allotment, e.g. 25 minutes',
  agenda_review: 'confirm to save or edit',
}

const DEFAULT_SAMPLE_RATE = 16000
const STT_KEEPALIVE_MS = 2000
const MIC_BLOCKED_MESSAGE =
  'Microphone access is blocked. Your organization may restrict browser microphone permissions. Use typing mode or ask IT to allow microphone access for this site.'
const BROWSER_UNSUPPORTED_MESSAGE =
  'Voice mode currently supports Chrome/Edge. Please switch browsers or use typing mode.'

function encodePcm16(float32Array) {
  const buffer = new ArrayBuffer(float32Array.length * 2)
  const view = new DataView(buffer)
  for (let i = 0; i < float32Array.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]))
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
  }
  return buffer
}

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function buildVoiceCommandRegex(commands) {
  const list = commands
    .filter((command) => typeof command === 'string' && command.trim().length > 0)
    .sort((a, b) => b.length - a.length)
  if (list.length === 0) return null
  const pattern = list
    .map((command) =>
      command
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .map((token) => escapeRegex(token))
        .join('\\s+')
    )
    .join('|')
  return new RegExp(`\\b(${pattern})\\b[\\s.!?]*$`, 'i')
}

function stripTrailingVoiceCommand(text, commandRegex) {
  const trimmed = text.trim()
  if (!commandRegex) return { cleanedText: trimmed, commandDetected: false, commandText: '' }
  const match = trimmed.match(commandRegex)
  if (!match) return { cleanedText: trimmed, commandDetected: false, commandText: '' }
  const cleanedText = trimmed.replace(commandRegex, '').trim()
  return {
    cleanedText,
    commandDetected: true,
    commandText: String(match[1] ?? '').trim().toLowerCase(),
  }
}

function normalizeMeetingTypeInput(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function tokenizeMeetingType(value) {
  return normalizeMeetingTypeInput(value)
    .split('_')
    .filter(Boolean)
}

function toTokenSet(value) {
  return new Set(tokenizeMeetingType(value))
}

function jaccardSimilarity(a, b) {
  if (!a.size || !b.size) return 0
  let intersection = 0
  a.forEach((token) => {
    if (b.has(token)) intersection += 1
  })
  const union = new Set([...a, ...b]).size
  return union === 0 ? 0 : intersection / union
}

function levenshteinDistance(a, b) {
  const rows = a.length + 1
  const cols = b.length + 1
  const dp = Array.from({ length: rows }, () => new Array(cols).fill(0))
  for (let i = 0; i < rows; i += 1) dp[i][0] = i
  for (let j = 0; j < cols; j += 1) dp[0][j] = j
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      )
    }
  }
  return dp[rows - 1][cols - 1]
}

function similarityScore(a, b) {
  if (!a || !b) return 0
  const distance = levenshteinDistance(a, b)
  const maxLen = Math.max(a.length, b.length)
  if (maxLen === 0) return 0
  return 1 - distance / maxLen
}

function scoreMeetingTypeMatch(input, type) {
  if (!type) return 0
  const normalizedInput = normalizeMeetingTypeInput(input)
  if (!normalizedInput) return 0
  const id = typeof type.id === 'string' ? type.id : ''
  const name = typeof type.name === 'string' ? type.name : (type.displayName ?? '')
  const normalizedId = normalizeMeetingTypeInput(id)
  const normalizedName = normalizeMeetingTypeInput(name)

  if (normalizedInput === normalizedId || normalizedInput === normalizedName) return 1

  let best = 0
  if (normalizedId && normalizedInput.includes(normalizedId)) best = Math.max(best, 0.9)
  if (normalizedName && normalizedInput.includes(normalizedName)) best = Math.max(best, 0.9)

  const inputTokens = toTokenSet(normalizedInput)
  best = Math.max(
    best,
    jaccardSimilarity(inputTokens, toTokenSet(normalizedId)),
    jaccardSimilarity(inputTokens, toTokenSet(normalizedName))
  )

  const collapsedInput = normalizedInput.replace(/_/g, '')
  best = Math.max(
    best,
    similarityScore(collapsedInput, normalizedId.replace(/_/g, '')),
    similarityScore(collapsedInput, normalizedName.replace(/_/g, ''))
  )

  return best
}

function resolveMeetingTypeOption(input, meetingTypes) {
  if (!input || meetingTypes.length === 0) return null
  let best = null
  let bestScore = 0

  meetingTypes.forEach((type) => {
    const score = scoreMeetingTypeMatch(input, type)
    if (score > bestScore) {
      bestScore = score
      best = type
    }
  })

  if (!best || bestScore < 0.4) return null
  return best
}

function toEditableText(value) {
  return decodeHtmlEntities(String(value ?? '')).trim()
}

const QUARTER_WORD_MAP = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
}

function formatMeetingTitle(value) {
  const decoded = decodeHtmlEntities(String(value ?? '')).trim()
  if (!decoded) return ''
  const words = decoded.split(/\s+/)
  const formatted = []
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i]
    const match = word.match(/^([^a-z0-9]*)([a-z0-9]+)([^a-z0-9]*)$/i)
    const prefix = match?.[1] ?? ''
    const core = match?.[2] ?? word
    const suffix = match?.[3] ?? ''
    const lower = core.toLowerCase()
    const nextRaw = words[i + 1]
    const nextCore = nextRaw ? nextRaw.replace(/[^a-z0-9]/gi, '').toLowerCase() : ''
    if (lower === 'q' && nextCore && (nextCore in QUARTER_WORD_MAP || /^[1-4]$/.test(nextCore))) {
      const digit = QUARTER_WORD_MAP[nextCore] ?? nextCore
      formatted.push(`${prefix}Q${digit}${suffix}`)
      i += 1
      continue
    }
    const quarterMatch = lower.match(/^q([1-4])$/)
    if (quarterMatch) {
      formatted.push(`${prefix}Q${quarterMatch[1]}${suffix}`)
      continue
    }
    if (!core) {
      formatted.push(word)
      continue
    }
    if (/\d/.test(core)) {
      formatted.push(`${prefix}${core.toUpperCase()}${suffix}`)
      continue
    }
    if (core === core.toUpperCase() && core.length > 1) {
      formatted.push(`${prefix}${core}${suffix}`)
      continue
    }
    formatted.push(`${prefix}${core.charAt(0).toUpperCase()}${core.slice(1).toLowerCase()}${suffix}`)
  }
  return formatted.join(' ')
}

function formatAgendaItemTitle(value) {
  const decoded = decodeHtmlEntities(String(value ?? '')).trim()
  if (!decoded) return ''
  const lower = decoded.toLowerCase()
  const chars = lower.split('')
  for (let i = 0; i < chars.length; i += 1) {
    if (/[a-z]/i.test(chars[i])) {
      chars[i] = chars[i].toUpperCase()
      break
    }
  }
  return chars.join('')
}

function formatDurationLabel(durationSec) {
  if (!durationSec || durationSec <= 0) return '-'
  const totalMinutes = Math.round(durationSec / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) {
    return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`
  }
  return `${totalMinutes} min`
}

function checkVoiceSupport() {
  if (!navigator?.mediaDevices?.getUserMedia) {
    return { supported: false, message: BROWSER_UNSUPPORTED_MESSAGE }
  }
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext
  if (!AudioContextCtor || typeof AudioContextCtor.prototype.createScriptProcessor !== 'function') {
    return { supported: false, message: BROWSER_UNSUPPORTED_MESSAGE }
  }
  return { supported: true, message: '' }
}

function VoiceAgendaPage() {
  const { meetingId } = useParams()
  const toast = useToast()
  const toastRef = useRef(toast)
  const audioRef = useRef(null)
  const audioUrlRef = useRef(null)
  const ttsRequestIdRef = useRef(0)
  const ttsAbortRef = useRef(null)
  const ttsResolveRef = useRef(null)
  const ttsProfileRef = useRef(DEFAULT_TTS_PROFILE)
  const speakingRef = useRef(false)
  const loadingRef = useRef(false)
  const sttRef = useRef({
    ws: null,
    stream: null,
    audioContext: null,
    processor: null,
    source: null,
    gain: null,
    paused: false,
    closing: false,
    lastKeepAliveAt: 0,
    silenceBuffer: null,
  })
  const sttManualCloseRef = useRef(false)
  const transcriptBufferRef = useRef('')
  const sessionStateRef = useRef(null)

  const [sessionInfo, setSessionInfo] = useState(null)
  const [sessionState, setSessionState] = useState(null)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [interimTranscript, setInterimTranscript] = useState('')
  const [loading, setLoading] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [sttStatus, setSttStatus] = useState('idle')
  const [voiceSupportMessage, setVoiceSupportMessage] = useState('')
  const [voiceSupported, setVoiceSupported] = useState(true)
  const [meetingTypes, setMeetingTypes] = useState([])
  const [meetingTypesLoading, setMeetingTypesLoading] = useState(true)
  const [detailsEditMode, setDetailsEditMode] = useState(false)
  const [detailsTitle, setDetailsTitle] = useState('')
  const [detailsDurationMinutes, setDetailsDurationMinutes] = useState('')
  const [detailsRecurring, setDetailsRecurring] = useState(null)
  const [detailsAgendaDraft, setDetailsAgendaDraft] = useState([])

  useEffect(() => {
    toastRef.current = toast
  }, [toast])

  useEffect(() => {
    let isActive = true
    getTtsProfileClient()
      .then((profile) => {
        // #region agent log
        fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'b5379e'},body:JSON.stringify({sessionId:'b5379e',location:'VoiceAgendaPage.jsx:profileLoaded',message:'TTS profile client resolved',data:{voice:profile.voice,outputFormat:profile.outputFormat},timestamp:Date.now(),hypothesisId:'H2'})}).catch(()=>{});
        // #endregion
        if (isActive) {
          ttsProfileRef.current = profile
        }
      })
      .catch(() => {})
    return () => {
      isActive = false
    }
  }, [])

  useEffect(() => {
    sessionStateRef.current = sessionState
  }, [sessionState])

  useEffect(() => {
    speakingRef.current = speaking
  }, [speaking])

  useEffect(() => {
    if (!sessionState || detailsEditMode) return
    const metadata = sessionState.meetingMetadata ?? {}
    setDetailsTitle(metadata.meetingTitle ? toEditableText(metadata.meetingTitle) : '')
    setDetailsDurationMinutes(
      metadata.meetingDurationSec ? String(Math.round(metadata.meetingDurationSec / 60)) : ''
    )
    setDetailsRecurring(typeof metadata.recurring === 'boolean' ? metadata.recurring : null)
    setDetailsAgendaDraft(
      (sessionState.agendaDraft ?? []).map((item) => ({
        id: item.id,
        title: toEditableText(item.title),
        durationMinutes: item.plannedDurationSec ? String(Math.round(item.plannedDurationSec / 60)) : '',
      }))
    )
  }, [detailsEditMode, sessionState])

  const displayMessages = useMemo(
    () => messages.map((message, index) => ({ ...message, _index: index })).reverse(),
    [messages]
  )

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  useEffect(() => {
    let active = true
    setMeetingTypesLoading(true)
    meetingsAPI.getPresets()
      .then((res) => {
        if (!active) return
        const presets = res?.data?.presets ?? res?.presets ?? []
        const normalized = presets
          .map((preset) => ({
            id: preset.id,
            name: preset.name || preset.displayName || preset.id,
            description: preset.description || '',
          }))
          .filter((preset) => preset.id && preset.name)
        setMeetingTypes(normalized)
      })
      .catch(() => {
        if (active) setMeetingTypes([])
      })
      .finally(() => {
        if (active) setMeetingTypesLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const commitCommands = getVoiceAgendaCommitCommands()
  const clearCommands = getVoiceAgendaClearCommands()
  const commitCommandLabel = formatVoiceAgendaCommitCommands(commitCommands)
  const clearCommandLabel = formatVoiceAgendaClearCommands(clearCommands)
  const commitCommandRegex = buildVoiceCommandRegex(commitCommands)
  const clearCommandRegex = buildVoiceCommandRegex(clearCommands)

  const currentStep = sessionState?.step ?? 'meeting_type'
  const currentStepLabel = stepLabels[currentStep] ?? 'Agenda'

  const resetStt = useCallback((nextStatus = 'idle') => {
    const sttState = sttRef.current
    sttState.paused = true
    sttState.closing = true
    sttManualCloseRef.current = true
    if (sttState.processor) {
      sttState.processor.disconnect()
    }
    if (sttState.source) {
      sttState.source.disconnect()
    }
    if (sttState.gain) {
      sttState.gain.disconnect()
    }
    if (sttState.audioContext) {
      sttState.audioContext.close().catch(() => {})
    }
    if (sttState.stream) {
      sttState.stream.getTracks().forEach((track) => track.stop())
    }
    if (sttState.ws) {
      if (sttState.ws.readyState === WebSocket.OPEN) {
        sttState.ws.send(JSON.stringify({ type: 'stop' }))
      }
      sttState.ws.close()
    }
    sttRef.current = {
      ws: null,
      stream: null,
      audioContext: null,
      processor: null,
      source: null,
      gain: null,
      paused: false,
      closing: false,
      lastKeepAliveAt: 0,
      silenceBuffer: null,
    }
    transcriptBufferRef.current = ''
    setInterimTranscript('')
    setSttStatus(nextStatus)
  }, [])

  const pauseStt = useCallback(() => {
    sttRef.current.paused = true
  }, [])

  const resumeStt = useCallback(() => {
    sttRef.current.paused = false
  }, [])

  const resolveActiveTts = useCallback(() => {
    if (ttsResolveRef.current) {
      ttsResolveRef.current()
      ttsResolveRef.current = null
    }
  }, [])

  const cancelPendingTts = useCallback((invalidate = true) => {
    if (invalidate) {
      ttsRequestIdRef.current += 1
    }
    if (ttsAbortRef.current) {
      ttsAbortRef.current.abort()
      ttsAbortRef.current = null
    }
  }, [])

  const stopAudioPlayback = useCallback(
    (resumeAfter = true) => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
        audioRef.current = null
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current)
        audioUrlRef.current = null
      }
      setSpeaking((prev) => (prev ? false : prev))
      speakingRef.current = false
      if (resumeAfter) {
        resumeStt()
      }
    },
    [resumeStt]
  )

  const stopAssistantAudio = useCallback(
    (resumeAfter = true) => {
      cancelPendingTts(true)
      stopAudioPlayback(resumeAfter)
      resolveActiveTts()
    },
    [cancelPendingTts, resolveActiveTts, stopAudioPlayback]
  )

  const skipAssistantSpeech = useCallback(() => {
    stopAssistantAudio(true)
  }, [stopAssistantAudio])

  const playAssistant = useCallback(async (text) => {
    if (!text) return
    // #region agent log
    fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'b5379e'},body:JSON.stringify({sessionId:'b5379e',location:'VoiceAgendaPage.jsx:playAssistant:entry',message:'speak using ref profile',data:{refVoice:ttsProfileRef.current?.voice,textLen:typeof text==='string'?text.length:0},timestamp:Date.now(),hypothesisId:'H1'})}).catch(()=>{});
    // #endregion
    const requestId = ttsRequestIdRef.current + 1
    ttsRequestIdRef.current = requestId
    cancelPendingTts(false)
    resolveActiveTts()
    try {
      stopAudioPlayback(false)
      pauseStt()
      setSpeaking(true)
      speakingRef.current = true
      const donePromise = new Promise((resolve) => {
        ttsResolveRef.current = resolve
      })
      const controller = new AbortController()
      ttsAbortRef.current = controller
      const profile = ttsProfileRef.current
      const { blob } = await ttsApi.speak({
        text,
        voice: profile.voice,
        format: profile.outputFormat,
        speed: profile.speed,
        signal: controller.signal,
        provider: 'elevenlabs',
      })
      if (controller.signal.aborted || requestId !== ttsRequestIdRef.current) {
        if (ttsAbortRef.current === controller) {
          ttsAbortRef.current = null
        }
        resolveActiveTts()
        return
      }
      const url = URL.createObjectURL(blob)
      audioUrlRef.current = url
      const audio = new Audio(url)
      // Speed was baked into the API-generated audio; avoid double-application by using playbackRate 1
      applyTtsPlaybackSettings(audio, profile, { playbackRate: 1 })
      audioRef.current = audio
      const finalizePlayback = () => {
        URL.revokeObjectURL(url)
        if (audioUrlRef.current === url) {
          audioUrlRef.current = null
        }
        if (audioRef.current === audio) {
          audioRef.current = null
        }
        if (ttsAbortRef.current === controller) {
          ttsAbortRef.current = null
        }
        if (requestId !== ttsRequestIdRef.current) {
          resolveActiveTts()
          return
        }
        setSpeaking(false)
        speakingRef.current = false
        resumeStt()
        resolveActiveTts()
      }
      audio.onended = finalizePlayback
      audio.onerror = finalizePlayback
      audio.play().catch(() => {
        finalizePlayback()
      })
      await donePromise
    } catch (err) {
      if (err?.name === 'AbortError' || requestId !== ttsRequestIdRef.current) {
        if (ttsAbortRef.current && ttsAbortRef.current.signal?.aborted) {
          ttsAbortRef.current = null
        }
        resolveActiveTts()
        return
      }
      setSpeaking(false)
      speakingRef.current = false
      if (ttsAbortRef.current && ttsAbortRef.current.signal?.aborted) {
        ttsAbortRef.current = null
      }
      resumeStt()
      resolveActiveTts()
    }
  }, [cancelPendingTts, pauseStt, resolveActiveTts, resumeStt, stopAudioPlayback])

  /**
   * Pre-recorded welcome from `public/audio` — avoids TTS API on first paint (see voiceAgendaWelcome.ts).
   * @returns ok = played through; failed = no HTMLAudio / decode or play error (caller may use server TTS); superseded = cancelled or unmounted
   */
  const playBundledWelcome = useCallback(async () => {
    if (typeof Audio === 'undefined') {
      return 'failed'
    }
    // #region agent log
    fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'b5379e'},body:JSON.stringify({sessionId:'b5379e',location:'VoiceAgendaPage.jsx:playBundledWelcome',message:'bundled welcome asset (not API TTS)',data:{refVoice:ttsProfileRef.current?.voice},timestamp:Date.now(),hypothesisId:'H4'})}).catch(()=>{});
    // #endregion
    const requestId = ttsRequestIdRef.current + 1
    ttsRequestIdRef.current = requestId
    cancelPendingTts(false)
    resolveActiveTts()
    let heardEnd = false
    let loadOrPlayFailed = false
    try {
      stopAudioPlayback(false)
      pauseStt()
      setSpeaking(true)
      speakingRef.current = true
      const donePromise = new Promise((resolve) => {
        ttsResolveRef.current = resolve
      })
      const controller = new AbortController()
      ttsAbortRef.current = controller
      const profile = ttsProfileRef.current
      const audio = new Audio(VOICE_AGENDA_WELCOME_AUDIO_URL)
      applyTtsPlaybackSettings(audio, profile, { playbackRate: 1 })
      audioRef.current = audio
      const finalizePlayback = () => {
        controller.signal.removeEventListener('abort', onAbort)
        if (audioRef.current === audio) {
          audioRef.current = null
        }
        if (ttsAbortRef.current === controller) {
          ttsAbortRef.current = null
        }
        if (requestId !== ttsRequestIdRef.current) {
          resolveActiveTts()
          return
        }
        setSpeaking(false)
        speakingRef.current = false
        resumeStt()
        resolveActiveTts()
      }
      const onAbort = () => {
        audio.pause()
        finalizePlayback()
      }
      controller.signal.addEventListener('abort', onAbort)
      audio.onended = () => {
        heardEnd = true
        finalizePlayback()
      }
      audio.onerror = () => {
        loadOrPlayFailed = true
        finalizePlayback()
      }
      audio.play().catch(() => {
        loadOrPlayFailed = true
        finalizePlayback()
      })
      await donePromise
      if (requestId !== ttsRequestIdRef.current) {
        return 'superseded'
      }
      if (heardEnd) {
        return 'ok'
      }
      if (loadOrPlayFailed) {
        return 'failed'
      }
      return 'superseded'
    } catch (err) {
      if (err?.name === 'AbortError' || requestId !== ttsRequestIdRef.current) {
        if (ttsAbortRef.current && ttsAbortRef.current.signal?.aborted) {
          ttsAbortRef.current = null
        }
        resolveActiveTts()
        return 'superseded'
      }
      setSpeaking(false)
      speakingRef.current = false
      if (ttsAbortRef.current && ttsAbortRef.current.signal?.aborted) {
        ttsAbortRef.current = null
      }
      resumeStt()
      resolveActiveTts()
      return requestId === ttsRequestIdRef.current ? 'failed' : 'superseded'
    }
  }, [cancelPendingTts, pauseStt, resolveActiveTts, resumeStt, stopAudioPlayback])

  useEffect(() => {
    const support = checkVoiceSupport()
    if (!support.supported) {
      setVoiceSupported(false)
      setVoiceSupportMessage(support.message)
      setSttStatus('unsupported')
    }
    let active = true
    const bootstrapSession = async () => {
      setLoading(true)
      loadingRef.current = true
      try {
        const payload = await agendaVoiceApi.createSession(meetingId)
        if (!active) return
        const data = payload?.data ?? payload
        setSessionInfo({ sessionId: data.sessionId, meetingId: data.meetingId })
        setSessionState(data.sessionState)
        setMessages([{ role: 'assistant', text: VOICE_AGENDA_WELCOME_MESSAGE }])
        if (active) {
          setLoading(false)
          loadingRef.current = false
        }
        const welcomePlayback = await playBundledWelcome()
        if (active && welcomePlayback === 'failed') {
          await playAssistant(VOICE_AGENDA_WELCOME_MESSAGE)
        }
      } catch (err) {
        if (active) {
          toastRef.current.error('Unable to start voice agenda session')
        }
      } finally {
        if (active) {
          setLoading(false)
          loadingRef.current = false
        }
      }
    }
    bootstrapSession()
    return () => {
      active = false
      stopAssistantAudio(false)
      resetStt('idle')
    }
  }, [meetingId, playAssistant, playBundledWelcome, resetStt, stopAssistantAudio])

  const stopStt = useCallback(() => {
    resetStt('idle')
  }, [resetStt])

  const finalizeAgenda = useCallback(async () => {
    if (!sessionInfo) return
    setLoading(true)
    try {
      const payload = await agendaVoiceApi.finalize(sessionInfo.sessionId)
      const data = payload?.data ?? payload
      setSessionState((prev) => (prev ? { ...prev, step: 'finalized' } : prev))
      stopStt()
      toast.success('Agenda saved', `Meeting ${data.meetingId}`)
    } catch (err) {
      toast.error('Unable to finalize agenda')
    } finally {
      setLoading(false)
    }
  }, [sessionInfo, stopStt, toast])

  const sendTurn = useCallback(
    async (overrideText, commitMode = 'manual_click', options = {}) => {
      const currentSession = sessionStateRef.current ?? sessionState
      if (!sessionInfo || !currentSession) return
      if (loadingRef.current || speakingRef.current) return
      const autoFinalize = Boolean(options?.autoFinalize)
      const rawText = (overrideText ?? inputValue).trim()
      if (!rawText) return
      let textForServer = rawText
      let displayText = rawText
      if (currentSession.step === 'meeting_type') {
        if (meetingTypesLoading) {
          toast.warning('Meeting types are still loading. Please wait.')
          return
        }
        const resolved = resolveMeetingTypeOption(rawText, meetingTypes)
        if (!resolved) {
          toast.error('Please choose a meeting type from the list.')
          return
        }
        displayText = resolved.name || rawText
        textForServer = resolved.id || resolved.name || rawText
      }

      setLoading(true)
      loadingRef.current = true
      setInputValue('')
      transcriptBufferRef.current = ''
      setInterimTranscript('')
      setMessages((prev) => [...prev, { role: 'user', text: displayText }])
      try {
        const payload = await agendaVoiceApi.sendTurn(sessionInfo.sessionId, textForServer, {
          commitMode,
          stepAtCommit: currentSession.step,
        })
        const data = payload?.data ?? payload
        setSessionState(data.draftStateDelta)
        sessionStateRef.current = data.draftStateDelta
        setMessages((prev) => [...prev, { role: 'assistant', text: data.assistantText }])
        await playAssistant(data.assistantText)
        if (
          autoFinalize &&
          data?.draftStateDelta?.step === 'agenda_review' &&
          data?.draftStateDelta?.readyToFinalize
        ) {
          await finalizeAgenda()
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Please try again.'
        toast.error('Unable to process this step', message)
      } finally {
        setLoading(false)
        loadingRef.current = false
      }
    },
    [finalizeAgenda, inputValue, meetingTypes, meetingTypesLoading, playAssistant, sessionInfo, sessionState, toast]
  )

  const handleSttMessage = useCallback(
    (event) => {
      let payload
      try {
        payload = JSON.parse(event.data)
      } catch (err) {
        return
      }

      if (payload.type === 'ready') {
        setSttStatus('active')
        return
      }

      if (payload.type === 'partial') {
        setInterimTranscript(payload.text ?? '')
        return
      }

      if (payload.type === 'final') {
        if (loadingRef.current || speakingRef.current) return
        setInterimTranscript('')
        const text = String(payload.text ?? '').trim()
        if (!text) return
        const nextBuffer = transcriptBufferRef.current ? `${transcriptBufferRef.current} ${text}` : text
        const { commandDetected: clearDetected } = stripTrailingVoiceCommand(nextBuffer, clearCommandRegex)

        if (clearDetected) {
          transcriptBufferRef.current = ''
          setInputValue('')
          setInterimTranscript('')
          toast.info('Cleared response')
          return
        }
        const {
          cleanedText,
          commandDetected,
          commandText,
        } = stripTrailingVoiceCommand(nextBuffer, commitCommandRegex)

        if (commandDetected) {
          transcriptBufferRef.current = ''
          if (cleanedText) {
            void sendTurn(cleanedText, 'explicit_phrase')
            return
          }
          const normalizedCommand = commandText?.toLowerCase() ?? ''
          const step = sessionStateRef.current?.step ?? sessionState?.step
          if (step === 'agenda_item_capture' || step === 'agenda_item_duration') {
            if (normalizedCommand === 'done adding items') {
              void sendTurn('done adding items', 'explicit_phrase')
            } else {
              setInputValue('')
            }
            return
          }
          if (step === 'agenda_review') {
            void sendTurn(normalizedCommand || 'confirm', 'explicit_phrase', { autoFinalize: true })
            return
          }
          setInputValue('')
          return
        }

        transcriptBufferRef.current = nextBuffer
        setInputValue(nextBuffer)
        return
      }

      if (payload.type === 'error') {
        const errMsg = payload.message || payload.code || 'Voice STT unavailable'
        console.error('[STT] Server error:', { code: payload.code, message: payload.message, full: payload })
        toast.error(`STT: ${errMsg}`)
        setVoiceSupportMessage('')
        resetStt('fallback')
      }
    },
    [clearCommandRegex, commitCommandRegex, resetStt, sendTurn, sessionState, toast]
  )

  const startStt = useCallback(async () => {
    if (!sessionInfo?.sessionId) {
      toast.error('Start a session before enabling voice')
      return
    }
    if (!voiceSupported) {
      toast.error(voiceSupportMessage || BROWSER_UNSUPPORTED_MESSAGE)
      return
    }
    if (speakingRef.current || audioRef.current) {
      stopAssistantAudio(true)
    }
    if (sttRef.current.ws) return
    sttManualCloseRef.current = false
    if (!navigator?.mediaDevices?.getUserMedia) {
      setVoiceSupportMessage(BROWSER_UNSUPPORTED_MESSAGE)
      toast.error(BROWSER_UNSUPPORTED_MESSAGE)
      resetStt('fallback')
      return
    }

    setSttStatus('connecting')
    transcriptBufferRef.current = ''
    setInterimTranscript('')

    const apiError = await checkApiReachable()
    if (apiError) {
      const wsUrl = getSttWebSocketUrl(sessionInfo.sessionId, sessionInfo.meetingId)
      console.error('[STT] API unreachable before WebSocket:', apiError, { wsUrl })
      toast.error(
        `Cannot reach API server: ${apiError}. Is it running on port 5000? Check VITE_API_URL in web-dashboard/.env`
      )
      resetStt('fallback')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      setVoiceSupportMessage('')
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext
      if (!AudioContextCtor) {
        setVoiceSupportMessage(BROWSER_UNSUPPORTED_MESSAGE)
        toast.error(BROWSER_UNSUPPORTED_MESSAGE)
        resetStt('fallback')
        return
      }
      const audioContext = new AudioContextCtor({ sampleRate: DEFAULT_SAMPLE_RATE })
      await audioContext.resume()

      const source = audioContext.createMediaStreamSource(stream)
      // Safari support is not targeted for v1 voice capture.
      const processor = audioContext.createScriptProcessor(4096, 1, 1)
      const gain = audioContext.createGain()
      gain.gain.value = 0

      source.connect(processor)
      processor.connect(gain)
      gain.connect(audioContext.destination)

      const ws = agendaVoiceSttApi.createSocket(sessionInfo.sessionId, sessionInfo.meetingId)
      sttRef.current.ws = ws
      sttRef.current.closing = false

      ws.onopen = () => {
        console.log('[STT] WebSocket connected, sending start...')
        ws.send(
          JSON.stringify({
            type: 'start',
            sessionId: sessionInfo.sessionId,
            meetingId: sessionInfo.meetingId,
            sampleRate: audioContext.sampleRate || DEFAULT_SAMPLE_RATE,
            encoding: 'pcm_s16le',
            languageCode: 'en',
          })
        )
      }
      ws.onmessage = handleSttMessage
      ws.onerror = (event) => {
        const wsUrl = getSttWebSocketUrl(sessionInfo.sessionId, sessionInfo.meetingId)
        console.error('[STT] WebSocket connection failed:', {
          url: wsUrl,
          event,
          hint: 'Check that the API server is running and VITE_API_URL matches (web-dashboard/.env)',
        })
        toast.error(
          `STT WebSocket failed to connect to ${new URL(wsUrl).origin}. Is the API server running?`
        )
        resetStt('fallback')
      }
      ws.onclose = (event) => {
        if (sttManualCloseRef.current || sttRef.current.closing) {
          sttManualCloseRef.current = false
          return
        }
        console.warn('[STT] WebSocket closed unexpectedly:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        })
        toast.error(`Voice STT disconnected (${event.code}: ${event.reason || 'connection closed'})`)
        resetStt('fallback')
      }

      processor.onaudioprocess = (event) => {
        const sttState = sttRef.current
        if (!sttState.ws || sttState.ws.readyState !== WebSocket.OPEN) return
        const inputData = event.inputBuffer.getChannelData(0)
        if (sttState.paused) {
          const now = Date.now()
          if (now - sttState.lastKeepAliveAt < STT_KEEPALIVE_MS) return
          if (!sttState.silenceBuffer || sttState.silenceBuffer.length !== inputData.length) {
            sttState.silenceBuffer = new Float32Array(inputData.length)
          }
          sttState.ws.send(encodePcm16(sttState.silenceBuffer))
          sttState.lastKeepAliveAt = now
          return
        }
        sttState.ws.send(encodePcm16(inputData))
      }

      sttRef.current.stream = stream
      sttRef.current.audioContext = audioContext
      sttRef.current.processor = processor
      sttRef.current.source = source
      sttRef.current.gain = gain
    } catch (err) {
      setVoiceSupportMessage(MIC_BLOCKED_MESSAGE)
      toast.error(MIC_BLOCKED_MESSAGE)
      resetStt('fallback')
    }
  }, [handleSttMessage, resetStt, sessionInfo, stopAssistantAudio, toast, voiceSupportMessage, voiceSupported])

  const beginDetailsEdit = useCallback(() => {
    if (!sessionState) return
    setDetailsEditMode(true)
    const metadata = sessionState.meetingMetadata ?? {}
    setDetailsTitle(metadata.meetingTitle ? toEditableText(metadata.meetingTitle) : '')
    setDetailsDurationMinutes(
      metadata.meetingDurationSec ? String(Math.round(metadata.meetingDurationSec / 60)) : ''
    )
    setDetailsRecurring(typeof metadata.recurring === 'boolean' ? metadata.recurring : null)
    setDetailsAgendaDraft(
      (sessionState.agendaDraft ?? []).map((item) => ({
        id: item.id,
        title: toEditableText(item.title),
        durationMinutes: item.plannedDurationSec ? String(Math.round(item.plannedDurationSec / 60)) : '',
      }))
    )
  }, [sessionState])

  const cancelDetailsEdit = useCallback(() => {
    setDetailsEditMode(false)
  }, [])

  const updateDraftItem = useCallback((id, updates) => {
    setDetailsAgendaDraft((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    )
  }, [])

  const saveDetailsEdit = useCallback(async () => {
    if (!sessionInfo) return
    const meetingTitle = detailsTitle.trim()
    const durationMinutes = detailsDurationMinutes.trim()
    const durationValue = durationMinutes.length > 0 ? Number(durationMinutes) : null
    if (durationMinutes && (Number.isNaN(durationValue) || durationValue <= 0)) {
      toast.error('Meeting duration must be a positive number of minutes.')
      return
    }
    for (const item of detailsAgendaDraft) {
      const durationText = String(item.durationMinutes ?? '').trim()
      if (durationText.length === 0) continue
      const parsed = Number(durationText)
      if (Number.isNaN(parsed) || parsed <= 0) {
        toast.error('Agenda item durations must be positive numbers.')
        return
      }
    }

    setLoading(true)
    loadingRef.current = true
    try {
      const payload = await agendaVoiceApi.updateDraft(sessionInfo.sessionId, {
        meetingMetadata: {
          meetingTitle: meetingTitle || undefined,
          meetingDurationSec: durationValue ? Math.round(durationValue * 60) : undefined,
          recurring: typeof detailsRecurring === 'boolean' ? detailsRecurring : undefined,
        },
        agendaDraft: detailsAgendaDraft.map((item) => {
          const durationText = String(item.durationMinutes ?? '').trim()
          const parsed = durationText.length > 0 ? Number(durationText) : null
          return {
            id: item.id,
            title: item.title?.trim() || undefined,
            plannedDurationSec: parsed ? Math.round(parsed * 60) : null,
          }
        }),
      })
      const data = payload?.data ?? payload
      setSessionState(data.draftStateDelta)
      sessionStateRef.current = data.draftStateDelta
      if (data.assistantText) {
        setMessages((prev) => [...prev, { role: 'assistant', text: data.assistantText }])
        await playAssistant(data.assistantText)
      }
      if (data.accepted) {
        setDetailsEditMode(false)
        toast.success('Meeting details updated')
      } else {
        toast.warning('Update rejected', 'Please adjust the agenda durations.')
      }
    } catch (err) {
      toast.error('Unable to update meeting details')
    } finally {
      setLoading(false)
      loadingRef.current = false
    }
  }, [
    detailsAgendaDraft,
    detailsDurationMinutes,
    detailsRecurring,
    detailsTitle,
    playAssistant,
    sessionInfo,
    toast,
  ])

  const agendaItems = useMemo(() => sessionState?.agendaDraft ?? [], [sessionState])
  const meetingMetadata = sessionState?.meetingMetadata ?? {}
  const meetingDurationLabel = useMemo(
    () => formatDurationLabel(meetingMetadata.meetingDurationSec),
    [meetingMetadata.meetingDurationSec]
  )
  const recurringLabel =
    typeof meetingMetadata.recurring === 'boolean' ? (meetingMetadata.recurring ? 'Yes' : 'No') : '-'
  const meetingTypeLabel = useMemo(() => {
    const rawType = meetingMetadata.meetingType
    if (!rawType) return '-'
    const decoded = decodeHtmlEntities(String(rawType))
    const normalized = normalizeMeetingTypeInput(decoded)
    const match = meetingTypes.find((type) => {
      if (!type) return false
      const idNorm = normalizeMeetingTypeInput(type.id ?? '')
      const nameNorm = normalizeMeetingTypeInput(type.name ?? '')
      return normalized === idNorm || normalized === nameNorm
    })
    return match?.name || decoded
  }, [meetingMetadata.meetingType, meetingTypes])
  const meetingTitleLabel = meetingMetadata.meetingTitle
    ? formatMeetingTitle(meetingMetadata.meetingTitle)
    : '-'
  const pendingAgendaItemTitle = sessionState?.pendingAgendaItem?.title
    ? formatAgendaItemTitle(sessionState.pendingAgendaItem.title)
    : ''
  const micButtonClass = useMemo(() => {
    const base =
      'px-6 py-3 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed'
    if (sttStatus === 'active') {
      return `${base} bg-blue-600 text-white animate-pulse shadow-md`
    }
    if (sttStatus === 'connecting') {
      return `${base} bg-blue-500 text-white animate-pulse`
    }
    return `${base} bg-blue-600 text-white hover:bg-blue-500 shadow-sm`
  }, [sttStatus])
  const liveGuidance = `Use the microphone to dictate each response, then say ${commitCommandLabel} to send. Say ${clearCommandLabel} to clear the current response.`
  const agendaItemDurationGuidance = pendingAgendaItemTitle
    ? `Please add the allotted time for "${pendingAgendaItemTitle}" in minutes or hours.`
    : 'Please add the allotted time for this agenda item in minutes or hours.'
  const meetingTypeGuidance = meetingTypes.length > 0
    ? `Choose a meeting type from the list or say it out loud. ${liveGuidance}`
    : liveGuidance
  const guidanceMessage = voiceSupportMessage
    ? voiceSupportMessage
    : sttStatus === 'fallback'
      ? 'Voice STT is unavailable. Use typed input and commit each step manually.'
      : currentStep === 'meeting_type'
        ? meetingTypeGuidance
        : currentStep === 'agenda_item_duration'
          ? `${agendaItemDurationGuidance} ${liveGuidance}`
          : liveGuidance

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <header className="flex flex-col gap-2">
            <p className="text-sm uppercase tracking-wide text-gray-400">Voice Agenda Builder</p>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold text-gray-900">Voice-to-Voice Agenda</h1>
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                {currentStepLabel}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <button
                className={micButtonClass}
                onClick={sttStatus === 'active' || sttStatus === 'connecting' ? stopStt : startStt}
                disabled={loading || sttStatus === 'connecting' || !voiceSupported}
              >
                {sttStatus === 'active' || sttStatus === 'connecting' ? 'Stop Mic' : 'Start Mic'}
              </button>
              {currentStep === 'meeting_type' && (
                <button
                  className="btn-secondary"
                  onClick={skipAssistantSpeech}
                  disabled={loading || !speaking}
                >
                  Skip Welcome
                </button>
              )}
              {speaking && (
                <button
                  className="btn-secondary"
                  onClick={skipAssistantSpeech}
                  disabled={loading}
                >
                  Skip Voice
                </button>
              )}
            </div>
            <p className="text-gray-500">{guidanceMessage}</p>
          </header>

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="glass p-6 flex flex-col gap-4 min-h-[520px]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Session</p>
                  <p className="text-sm text-gray-700">Meeting ID: {sessionInfo?.meetingId ?? '-'}</p>
                </div>
                <div className="text-xs text-gray-400">Step: {currentStep}</div>
              </div>

              <div className="space-y-3">
                <input
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder={stepPlaceholders[currentStep] ?? 'Type your response'}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent-500"
                  disabled={loading || speaking}
                />
                {interimTranscript && (
                  <p className="text-xs text-gray-400">Listening: {interimTranscript}</p>
                )}
                {currentStep === 'agenda_item_duration' && (
                  <p className="text-xs text-gray-500">{agendaItemDurationGuidance}</p>
                )}
                {currentStep === 'meeting_type' && (
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-wide text-gray-400">Meeting Types</p>
                    {meetingTypesLoading ? (
                      <p className="mt-2 text-xs text-gray-500">Loading meeting types...</p>
                    ) : meetingTypes.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {meetingTypes.map((type) => (
                          <button
                            key={type.id}
                            type="button"
                            className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100"
                            onClick={() => setInputValue(type.name)}
                            disabled={loading || speaking}
                            title={type.description || type.name}
                          >
                            {type.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-gray-500">No meeting types available.</p>
                    )}
                    {!meetingTypesLoading && meetingTypes.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">Say one of these or click to insert it.</p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    className="btn-primary"
                    onClick={() => sendTurn()}
                    disabled={loading || speaking || !inputValue.trim()}
                  >
                    Commit Step
                  </button>
                  {currentStep === 'agenda_item_capture' && (
                    <button
                      className="btn-secondary"
                      onClick={() => sendTurn('done adding items')}
                      disabled={loading || speaking}
                    >
                      Done Adding Items
                    </button>
                  )}
                  {currentStep === 'agenda_review' && (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() => sendTurn('edit')}
                        disabled={loading || speaking}
                      >
                        Edit Agenda
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => sendTurn('confirm')}
                        disabled={loading || speaking || sessionState?.readyToFinalize}
                      >
                        Confirm Agenda
                      </button>
                      <button
                        className="btn-primary"
                        onClick={finalizeAgenda}
                        disabled={loading || speaking || !sessionState?.readyToFinalize}
                      >
                        Finalize & Save
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto pr-2">
                {displayMessages.map((message) => (
                  <div
                    key={`${message.role}-${message._index}`}
                    className={`rounded-xl p-3 text-sm ${
                      message.role === 'assistant'
                        ? 'bg-white border border-gray-200 text-gray-700'
                        : 'bg-gradient-primary text-white'
                    }`}
                  >
                    {message.text}
                  </div>
                ))}
              </div>
            </section>

            <section className="glass p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wide text-gray-400">Meeting Details</p>
                  {detailsEditMode ? (
                    <div className="flex gap-2">
                      <button
                        className="btn-secondary"
                        onClick={cancelDetailsEdit}
                        disabled={loading || speaking}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-primary"
                        onClick={saveDetailsEdit}
                        disabled={loading || speaking}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-ghost"
                      onClick={beginDetailsEdit}
                      disabled={loading || speaking}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-gray-400">Type</span>
                    <span className="text-gray-800">{meetingTypeLabel}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-gray-400">Title</span>
                    {detailsEditMode ? (
                      <input
                        value={detailsTitle}
                        onChange={(event) => setDetailsTitle(event.target.value)}
                        className="w-1/2 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800"
                        disabled={loading || speaking}
                      />
                    ) : (
                      <span className="text-gray-800">{meetingTitleLabel}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-gray-400">Duration</span>
                    {detailsEditMode ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={detailsDurationMinutes}
                          onChange={(event) => setDetailsDurationMinutes(event.target.value)}
                          className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800"
                          disabled={loading || speaking}
                        />
                        <span className="text-gray-500 text-xs">min</span>
                      </div>
                    ) : (
                      <span className="text-gray-800">{meetingDurationLabel}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-gray-400">Recurring</span>
                    {detailsEditMode ? (
                      <select
                        value={
                          typeof detailsRecurring === 'boolean' ? (detailsRecurring ? 'yes' : 'no') : ''
                        }
                        onChange={(event) => {
                          const value = event.target.value
                          setDetailsRecurring(value === '' ? null : value === 'yes')
                        }}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800"
                        disabled={loading || speaking}
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (
                      <span className="text-gray-800">{recurringLabel}</span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Agenda Draft</p>
                <div className="mt-3 space-y-3">
                  {agendaItems.length === 0 && (
                    <p className="text-sm text-gray-500">No agenda items yet.</p>
                  )}
                  {detailsEditMode
                    ? detailsAgendaDraft.map((item) => (
                        <div key={item.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
                          <input
                            value={item.title}
                            onChange={(event) => updateDraftItem(item.id, { title: event.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800"
                            disabled={loading || speaking}
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={item.durationMinutes}
                              onChange={(event) =>
                                updateDraftItem(item.id, { durationMinutes: event.target.value })
                              }
                              className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800"
                              disabled={loading || speaking}
                            />
                            <span className="text-xs text-gray-500">min</span>
                          </div>
                        </div>
                      ))
                    : agendaItems.map((item) => {
                        const title = formatAgendaItemTitle(item.title)
                        const description = item.description ? decodeHtmlEntities(String(item.description)) : ''
                        return (
                          <div key={item.id} className="border border-gray-200 rounded-xl p-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-800">{title}</p>
                              <span className="text-xs text-gray-500">
                                {item.plannedDurationSec ? Math.round(item.plannedDurationSec / 60) : '-'} min
                              </span>
                            </div>
                            {description && (
                              <p className="text-xs text-gray-500 mt-2 whitespace-pre-line">{description}</p>
                            )}
                          </div>
                        )
                      })}
                </div>
              </div>

            </section>
          </div>
        </div>
      </main>
    </div>
  )
}

export default VoiceAgendaPage
